// The getting-started checklist: a small card floating bottom-right on every
// workspace page, for an admin whose workspace is not set up yet.
//
// Mounted once in AppLayout rather than on the Tasks page, so it stays in view
// while the person follows it into Settings. It hides itself when the
// workspace is set up and when it is closed. Admins, managers and users each
// get their own list; OnboardingContext decides all of it from the
// workspace's own data.
//
// The next step is the only expanded one: bold, with its explanation and a
// "Show me" that opens the page and points at the exact button, using the
// same Shepherd.js the guided tour runs on.
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckBox as DoneIcon,
  CheckBoxOutlineBlank as TodoIcon,
  Close as CloseIcon,
  // The card is anchored to the bottom of the screen, so minimising moves
  // it down and expanding moves it up. The chevrons point that way.
  ExpandMore as CollapseIcon,
  ExpandLess as ExpandIcon,
  RocketLaunch as RocketIcon,
} from '@mui/icons-material';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useTour } from '../contexts/TourContext';
import { APP_INSTALL_URL, APP_INSTALL_IS_TEST_BUILD } from '../constants/appInstall';

function workspaceCode() {
  try {
    return localStorage.getItem('subscriberCode') || '';
  } catch (err) {
    return '';
  }
}

function StepRow({ step, done, current, onOpen, onShowMe, setup }) {
  const code = workspaceCode();

  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        bgcolor: current ? 'action.hover' : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {done ? (
          <DoneIcon fontSize="small" color="primary" aria-label="Done" />
        ) : (
          <TodoIcon fontSize="small" color="action" aria-label="Not done yet" />
        )}
        <Typography
          variant="body2"
          onClick={() => onOpen(step)}
          sx={{
            flex: 1,
            cursor: 'pointer',
            fontWeight: current ? 700 : 400,
            textDecoration: done ? 'line-through' : 'none',
            color: done ? 'text.disabled' : 'text.primary',
          }}
        >
          {step.title}
          {step.optional && !done && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
              optional
            </Typography>
          )}
        </Typography>
        {current && (
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => onShowMe(step)}
            sx={{ py: 0.25, px: 1, minWidth: 0, fontSize: 12, textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Show me
          </Button>
        )}
      </Box>

      {current && (
        <Box sx={{ pl: 3.5, pt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ lineHeight: 1.45 }}>
            {step.description}
          </Typography>
          {step.installsApp && (
            <Typography variant="caption" component="p" sx={{ mt: 0.75, lineHeight: 1.6 }}>
              {code && (
                <>
                  Workspace code{' '}
                  <Box component="b" sx={{ fontFamily: 'monospace', fontSize: 13 }}>{code}</Box>
                  <br />
                </>
              )}
              {/* Only admins and managers can read a PIN, so for anyone else
                  signInPin is null and this line is simply absent. */}
              {setup?.signInPin && (
                <>
                  Then pick <b>{setup.signInOperatorName || 'Demo operator'}</b> with PIN{' '}
                  <Box component="b" sx={{ fontFamily: 'monospace', fontSize: 13 }}>{setup.signInPin}</Box>
                  <br />
                </>
              )}
              {/* Staging hands out a test build through Firebase App
                  Distribution: the link is a tester invite, and being added
                  is a step of its own. Production is a Play listing. */}
              <Link href={APP_INSTALL_URL} target="_blank" rel="noopener noreferrer">
                {APP_INSTALL_IS_TEST_BUILD ? 'Request the TrakLog Staging test build' : 'Get it on Google Play'}
              </Link>
              {APP_INSTALL_IS_TEST_BUILD && (
                <>
                  <br />
                  Open it and sign in with Google to join the testers. Firebase App Tester then
                  installs it and keeps it up to date.
                </>
              )}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    steps,
    progress,
    shouldShowOnboarding,
    dismissOnboarding,
    isStepCompleted,
    getNextStep,
    refreshSetup,
    setup,
  } = useOnboarding();
  const { spotlight } = useTour();
  const [expanded, setExpanded] = useState(true);

  // Re-read the workspace on every page change, so a step ticks itself off
  // when the person comes back from doing it. Cheap, and only while shown.
  useEffect(() => {
    if (shouldShowOnboarding) refreshSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (!shouldShowOnboarding) return null;

  const next = getNextStep();
  const required = steps.filter((s) => !s.optional);
  const doneCount = required.filter((s) => isStepCompleted(s.id)).length;

  const open = (step) => navigate(step.path);

  const showMe = (step) => {
    if (location.pathname !== step.path) navigate(step.path);
    if (step.spotlight) {
      spotlight(step.spotlight, { title: step.title, text: step.description });
    }
  };

  return (
    <Paper
      data-tour="onboarding-checklist"
      role="complementary"
      aria-label="Getting started"
      elevation={6}
      sx={{
        position: 'fixed',
        right: { xs: 16, sm: 24 },
        bottom: { xs: 16, sm: 24 },
        left: { xs: 16, sm: 'auto' },
        width: { sm: 360 },
        // Above page content, below MUI dialogs (1300) so a dialog the person
        // opens from "Show me" is never covered by the card.
        zIndex: 1250,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
        }}
      >
        <RocketIcon fontSize="small" color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            Get started with TrakLog
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {doneCount} of {required.length} done
          </Typography>
        </Box>
        <Tooltip title={expanded ? 'Minimise' : 'Expand'}>
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? 'Minimise the checklist' : 'Expand the checklist'}
          >
            {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Close. You can bring it back from Settings, User Guide.">
          <IconButton size="small" onClick={dismissOnboarding} aria-label="Close the checklist">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{ height: 3, bgcolor: 'action.hover' }}
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <Box sx={{ height: '100%', width: `${progress}%`, bgcolor: 'primary.main', transition: 'width 300ms' }} />
      </Box>

      <Collapse in={expanded}>
        <Box component="ul" sx={{ m: 0, p: 1 }}>
          {steps.map((step) => (
            <StepRow
              key={step.id}
              step={step}
              done={isStepCompleted(step.id)}
              current={next?.id === step.id}
              onOpen={open}
              onShowMe={showMe}
              setup={setup}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default OnboardingChecklist;
